import { Fragment, useEffect } from "react";
import { useShowContent } from "src/generator/hooks/useShowContent";
import { useTheme, useThemeFlags } from "src/hooks";
import type { ICol } from "src/interfaces";
import { getColValue, renderList } from "src/pages/Reports/Report/Report.utils";
import { useSelector } from "src/redux/hooks";

interface Props {
  col: ICol;
  rowIndex?: number;
  colIndex?: number;
  lbeid?: string;
  entryIndex?: number;
  pdfEntry?: boolean;
}

const ValueRenderer = ({
  col,
  rowIndex,
  colIndex,
  lbeid,
  entryIndex,
  pdfEntry,
}: Props) => {
  const { handleShowContent } = useShowContent();
  const users = useSelector(state => state.entriesGeneratorSlice.users);
  const yachtdata = useSelector(state => state.entriesGeneratorSlice.yachtdata);
  const checkLists = useSelector(
    state => state.entriesGeneratorSlice.checkLists,
  );
  const theme = useTheme() as any;
  const flags = useThemeFlags();
  const { uppercase } = useSelector(state => state.preferencesSlice);

  const colAny = col as any;

  const uniqueKey = `e_${entryIndex ?? ""}_r_${rowIndex ?? ""}_c_${colIndex ?? ""}`;

  // Handles showContent feature for unit="check" fields
  useEffect(() => {
    if (
      col.unit === "check" &&
      colAny.feature === "showContent" &&
      colAny.withClass
    ) {
      const isChecked = col.value === true || col.value === "true";
      handleShowContent(isChecked, colAny.withClass, uniqueKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [
    col.unit,
    colAny.feature,
    colAny.withClass,
    col.id,
    col.value,
    entryIndex,
  ]);

  // Handles showContent feature for select/list fields with onKey
  useEffect(() => {
    if (
      col.list &&
      col.list.length > 0 &&
      !["vital_list", "multilist", "searchlist"].includes(col.unit as string) &&
      colAny.feature === "showContent" &&
      colAny.withClass &&
      colAny.onKey
    ) {
      const currentKey = (col.value as any)?.key;
      const shouldShow = currentKey === colAny.onKey;
      const ownKey = `${colAny.withClass}${col.id ?? ""}`;
      handleShowContent(shouldShow, ownKey, uniqueKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [
    col.list,
    col.unit,
    colAny.feature,
    colAny.withClass,
    colAny.onKey,
    col.id,
    col.value,
    entryIndex,
  ]);

  if (flags.disableChecklists && col.unit === "checklist") return null;

  if (col.content && col.unit !== "guernseyMulti") {
    const direction =
      col.direction === "row" || col.content.flex === "row" ? "row" : "column";

    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: direction,
          gap: direction === "row" ? 16 : 8,
        }}>
        {(Array.isArray(col.content.cols)
          ? col.content.cols
          : Object.values(col.content.cols)
        ).map((c: any, i: number) => (
          <Fragment key={i}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                textDecoration: c.strikethrough ? "line-through" : "",
              }}>
              {!c.hideInReports && c.label && <span>{c.label ?? ""}</span>}
              {getColValue(
                c,
                users,
                theme.colors.main[9],
                yachtdata,
                uppercase,
                checkLists,
                rowIndex,
                lbeid,
                pdfEntry,
              )}
            </div>
            {i + 1 !==
              (Array.isArray(col.content?.cols)
                ? col.content.cols.length
                : Object.keys(col.content?.cols).length) &&
              direction !== "row" && (
                <div style={{ width: "100%", height: 1, background: "#ddd" }} />
              )}
          </Fragment>
        ))}
      </div>
    );
  } else if (col.unit === "check") {
    return getColValue(
      col,
      users,
      theme.colors.main[9],
      null,
      uppercase,
      undefined,
      rowIndex,
      lbeid,
      pdfEntry,
    );
  } else if (
    col.list &&
    col.list.length > 0 &&
    !["vital_list", "multilist", "searchlist"].includes(col.unit as string)
  ) {
    // Don't render list items for unit="list" or unit="vital_list" - they're handled in getColValue
    if (["list"].includes(col.unit as string)) {
      return (
        <div
          style={{
            margin: 0,
            fontWeight: col.bold ? "bold" : "normal",
            textDecoration: col.strikethrough ? "line-through" : "",
          }}>
          {getColValue(
            col,
            users,
            theme.colors.main[9],
            yachtdata,
            uppercase,
            checkLists,
            rowIndex,
            lbeid,
            pdfEntry,
          )}
        </div>
      );
    }

    return (
      <div style={{ textTransform: uppercase ? "uppercase" : "none" }}>
        {renderList(col.list, (col.value as any)?.key ?? undefined, col.suffix)}
      </div>
    );
  } else {
    return (
      <div
        style={{
          margin: 0,
          fontWeight: col.bold ? "bold" : "normal",
          textDecoration: col.strikethrough ? "line-through" : "",
        }}>
        {getColValue(
          col,
          users,
          theme.colors.main[9],
          yachtdata,
          uppercase,
          checkLists,
          rowIndex,
          lbeid,
          pdfEntry,
        )}
      </div>
    );
  }
};

export default ValueRenderer;
